import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { RbacService } from '../services/rbac/rbac.service';

function resolvePostLoginDestination(
  rbacService: RbacService,
  authService: AuthService
): string {
  const requested = authService.consumePostLoginRedirectUrl();
  const target = requested ?? '/device-list';
  return rbacService.canAccessRoute(target)
    ? target
    : rbacService.getDefaultRoute();
}

function completeSsoLogin(
  rbacService: RbacService,
  authService: AuthService,
  router: Router
) {
  const destination = resolvePostLoginDestination(rbacService, authService);
  router.navigateByUrl(destination, { replaceUrl: true });
}

function denyUnauthorizedRoute(
  stateUrl: string,
  rbacService: RbacService,
  authService: AuthService,
  router: Router
): false {
  const currentPath = stateUrl.split('?')[0];
  const fallback = rbacService.getDefaultRoute();

  if (fallback !== currentPath) {
    router.navigate([fallback], { replaceUrl: true });
    return false;
  }

  if (!rbacService.canAccessRoute(currentPath)) {
    router.navigate([authService.getLoginRoute()], { replaceUrl: true });
  }

  return false;
}

export const AuthGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const rbacService = inject(RbacService);
  const router = inject(Router);

  if (authService.hasSsoCallback()) {
    if (!authService.handleAuthCallback()) {
      router.navigate(['/login'], { replaceUrl: true });
      return false;
    }

    return authService.exchangeSsoToken().pipe(
      switchMap((exchanged) => {
        if (!exchanged) {
          router.navigate(['/login'], { replaceUrl: true });
          return of(false);
        }
        return rbacService.ensureContextLoaded().pipe(
          map(() => {
            completeSsoLogin(rbacService, authService, router);
            return true;
          })
        );
      })
    );
  }

  if (!authService.isAuthenticated()) {
    authService.setRedirectUrl(state.url);
    router.navigate([authService.getLoginRoute()]);
    return false;
  }

  if (rbacService.isContextReady()) {
    if (rbacService.canAccessRoute(state.url)) {
      return true;
    }
    return denyUnauthorizedRoute(state.url, rbacService, authService, router);
  }

  return rbacService.ensureContextLoaded().pipe(
    map(() => {
      if (rbacService.canAccessRoute(state.url)) {
        return true;
      }
      return denyUnauthorizedRoute(state.url, rbacService, authService, router);
    })
  );
};
